/**
 * useThrottledEffect Hook
 *
 * A throttled effect that executes at most once per specified delay.
 * Useful for autosave functionality to prevent excessive API calls.
 *
 * Based on LogicAppsUX useThrottledEffect pattern.
 */

import { useCallback, useEffect, useRef } from 'react';

/**
 * A throttled effect that runs at most once per `delay` milliseconds.
 *
 * Pass `Infinity` (or any non-positive / non-finite value) to disable the
 * effect entirely — no timer will be scheduled. This is the safe way for
 * callers to gate scheduling without ever risking `setTimeout` overflow
 * (browsers and Node both clamp delays > 2^31 - 1 back to 1ms, which would
 * otherwise produce a tight loop of pointless re-fires every render).
 *
 * Returns a `reset()` function that re-anchors the throttle window to
 * "now" AND clears any pending fire so the next execution waits a full
 * `delay`. Useful when an external signal (e.g. a clean → dirty
 * transition in autosave) should restart the throttle clock instead of
 * inheriting a stale `lastExecutionTime` that has drifted past `delay`
 * during idle — which would otherwise cause the leading-edge timer to
 * either fire immediately or, worse, no-op silently after `reset()`
 * bumped `lastExecutionTime` out from under it, leaving the throttle
 * with no scheduled fire until the next render.
 *
 * @param effect - The effect function to run
 * @param deps - Dependencies array (like useEffect)
 * @param delay - Minimum time between executions in milliseconds. Use
 *                `Infinity` (or `<= 0`) to disable scheduling.
 */
export const useThrottledEffect = (
  effect: () => void,
  deps: React.DependencyList,
  delay: number
): { reset: () => void } => {
  const lastExecutionTime = useRef(Date.now());
  // Tracks the currently-pending timeout so `reset()` can cancel it
  // before scheduling a fresh window. Without this, a `reset()` call
  // that landed after the main effect had already scheduled a 0ms timer
  // would leave that timer in the queue; when it fired, `execute` would
  // see `lastExecutionTime` had just been bumped, no-op, and never
  // schedule a replacement. The throttle would then go silent until
  // `callback` or `delay` changed and re-bound the effect.
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoize the effect to track dependency changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(effect, deps);

  // Stash the latest callback + delay in refs so the timer scheduled by
  // `reset()` (which lives outside the main effect's closure) always
  // calls the freshest function and respects the freshest delay if the
  // caller flips between configurations between reset() invocations.
  const callbackRef = useRef(callback);
  const delayRef = useRef(delay);
  useEffect(() => {
    callbackRef.current = callback;
    delayRef.current = delay;
  }, [callback, delay]);

  useEffect(() => {
    // Skip entirely when the caller has explicitly disabled scheduling
    // (Infinity / non-positive / NaN). Avoids the setTimeout-overflow trap.
    if (!Number.isFinite(delay) || delay <= 0) {
      timeoutIdRef.current = null;
      return;
    }

    const execute = () => {
      timeoutIdRef.current = null;
      const now = Date.now();
      const currentDelay = delayRef.current;
      if (now - lastExecutionTime.current >= currentDelay) {
        callbackRef.current();
        lastExecutionTime.current = now;
      }
    };

    // Calculate remaining time until next allowed execution
    const timeSinceLastExecution = Date.now() - lastExecutionTime.current;
    const remainingTime = Math.max(0, delay - timeSinceLastExecution);

    timeoutIdRef.current = setTimeout(execute, remainingTime);

    return () => {
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [callback, delay]);

  // Stable reset handle. Cancels any pending timer, re-anchors the
  // throttle window, and (when scheduling is enabled) schedules a fresh
  // full-delay timer so the next fire is guaranteed. Safe to call from
  // inside an effect — no React state changes here, so no re-render
  // loop risk.
  const reset = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    lastExecutionTime.current = Date.now();

    const currentDelay = delayRef.current;
    if (!Number.isFinite(currentDelay) || currentDelay <= 0) return;

    const execute = () => {
      timeoutIdRef.current = null;
      const now = Date.now();
      const d = delayRef.current;
      if (now - lastExecutionTime.current >= d) {
        callbackRef.current();
        lastExecutionTime.current = now;
      }
    };
    timeoutIdRef.current = setTimeout(execute, currentDelay);
  }, []);

  return { reset };
};

export default useThrottledEffect;
